import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DesignCheckContext } from "@sts/calc";
import { ApiError, ApiUnreachableError, type ApiClient } from "../api/client.js";

/**
 * What the plan's MCS design check needs from the API: the survey's heat loss
 * per room (with each room's scanned-room identity), its design conditions and
 * the job's design flow temperature — `context` from
 * `GET /api/surveys/:id/design-check` — and a way to set the flow temperature.
 *
 * The check itself is `computeDesignCheck` from @sts/calc, run in the plan on
 * every change so a radiator turns red or green as it is dragged; the API runs
 * the same function over the same context and stored objects, so the two agree.
 *
 * PREVIEW VERSUS SAVED. The flow-temperature control beside the plan moves a
 * slider, and every emitter in the house is re-rated as it moves. That is a
 * PREVIEW: nothing is written until the designer accepts it. So this hook
 * keeps two contexts and they are deliberately not interchangeable —
 *
 *   `context`         what is SAVED. Anything that writes a figure stamped
 *                     with the design flow temperature (the heat pump's
 *                     capacity at design conditions, for one) must read this,
 *                     or it would record a capacity against a temperature
 *                     nobody chose.
 *   `previewContext`  the same, with the slider's figures over the top. What
 *                     the live red/green check runs on. Equal to `context`
 *                     when nothing is being previewed.
 *
 * The design-check endpoint, the design pack and the installer pack all read
 * the saved decisions from the database and never see any of this.
 */
export interface DesignFlowSavedBy {
  readonly userId: string;
  /** Null when that person has since been removed from the account. */
  readonly name: string | null;
  readonly at: string | null;
}

export interface DesignCheckSource {
  load(): Promise<{
    readonly jobId: string;
    readonly context: DesignCheckContext;
    readonly savedBy?: DesignFlowSavedBy | null;
  }>;
  setDesignFlow(
    jobId: string,
    patch: { readonly designFlowTempC?: number; readonly systemDeltaTK?: number },
  ): Promise<void>;
}

export function designCheckFromApi(client: ApiClient, surveyId: string): DesignCheckSource {
  return {
    load: async () => {
      const res = await client.designCheck(surveyId);
      return { jobId: res.jobId, context: res.context, savedBy: res.designFlowSavedBy ?? null };
    },
    setDesignFlow: async (jobId, patch) => {
      await client.updateDesignDecisions(jobId, patch);
    },
  };
}

export type DesignCheckStatus = "unavailable" | "loading" | "ready" | "failed";

/** What the designer is trying out, before saving it. Null means "nothing". */
export interface DesignFlowPreview {
  readonly designFlowTempC: number | null;
  readonly systemDeltaTK: number | null;
}

export interface DesignCheckState {
  readonly status: DesignCheckStatus;
  /** The SAVED design conditions. */
  readonly context: DesignCheckContext | null;
  /** The saved conditions with the preview over the top — what the live check runs on. */
  readonly previewContext: DesignCheckContext | null;
  /** What is being previewed, if anything, and how it differs from what is saved. */
  readonly preview: DesignFlowPreview | null;
  /** True when the preview differs from the saved figures: the screen must say so. */
  readonly previewing: boolean;
  /** Who last saved the flow temperature, and when. Null when nobody has. */
  readonly savedBy: DesignFlowSavedBy | null;
  readonly jobId: string | null;
  readonly error: string | null;
  readonly saving: boolean;
  readonly reload: () => void;
  /** Tries a flow temperature and/or ΔT without saving anything. */
  readonly setPreview: (patch: Partial<DesignFlowPreview>) => void;
  /** Drops the preview and goes back to the saved figures. */
  readonly clearPreview: () => void;
  /** Saves the design flow temperature (and ΔT) on the job's design decisions, then re-reads the context. */
  readonly setDesignFlow: (patch: { designFlowTempC?: number; systemDeltaTK?: number }) => void;
}

function reasonOf(error: unknown): string {
  if (error instanceof ApiError || error instanceof ApiUnreachableError) return error.message;
  return "Something went wrong.";
}

export function useDesignCheckContext(
  source: DesignCheckSource | undefined,
  surveyId: string,
): DesignCheckState {
  const [status, setStatus] = useState<DesignCheckStatus>(source ? "loading" : "unavailable");
  const [context, setContext] = useState<DesignCheckContext | null>(null);
  const [savedBy, setSavedBy] = useState<DesignFlowSavedBy | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [nonce, setNonce] = useState(0);
  const [preview, setPreviewState] = useState<DesignFlowPreview | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    if (!source) {
      setStatus("unavailable");
      setContext(null);
      return;
    }
    let current = true;
    setStatus((now) => (now === "ready" ? now : "loading"));
    source.load().then(
      (loaded) => {
        if (!current) return;
        setContext(loaded.context);
        setSavedBy(loaded.savedBy ?? null);
        setJobId(loaded.jobId);
        setStatus("ready");
        setError(null);
      },
      (err: unknown) => {
        if (!current) return;
        setError(reasonOf(err));
        setStatus("failed");
      },
    );
    return () => {
      current = false;
    };
  }, [source, surveyId, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  const setPreview = useCallback<DesignCheckState["setPreview"]>((patch) => {
    setPreviewState((now) => ({
      designFlowTempC: patch.designFlowTempC ?? now?.designFlowTempC ?? null,
      systemDeltaTK: patch.systemDeltaTK ?? now?.systemDeltaTK ?? null,
    }));
  }, []);

  const clearPreview = useCallback(() => setPreviewState(null), []);

  const setDesignFlow = useCallback<DesignCheckState["setDesignFlow"]>(
    (patch) => {
      if (!source || !jobId) return;
      setSaving(true);
      setError(null);
      source.setDesignFlow(jobId, patch).then(
        () => {
          if (!alive.current) return;
          setSaving(false);
          // Saved: the preview and the saved figures now agree, so there is
          // nothing left to warn the designer about. The saved context is moved
          // to the figures just written BEFORE the re-read lands — otherwise
          // dropping the preview would, for one frame, show the old saved
          // temperature (or "not saved yet") for the value that was just saved.
          setContext((now) =>
            now === null
              ? now
              : {
                  ...now,
                  ...(patch.designFlowTempC !== undefined
                    ? { designFlowTempC: patch.designFlowTempC }
                    : {}),
                  ...(patch.systemDeltaTK !== undefined
                    ? { systemDeltaTK: patch.systemDeltaTK }
                    : {}),
                },
          );
          setPreviewState(null);
          setNonce((n) => n + 1);
        },
        (err: unknown) => {
          if (!alive.current) return;
          setSaving(false);
          setError(`The design flow temperature could not be saved. ${reasonOf(err)}`);
        },
      );
    },
    [source, jobId],
  );

  const previewContext = useMemo<DesignCheckContext | null>(() => {
    if (!context) return null;
    if (!preview) return context;
    return {
      ...context,
      ...(preview.designFlowTempC !== null ? { designFlowTempC: preview.designFlowTempC } : {}),
      ...(preview.systemDeltaTK !== null ? { systemDeltaTK: preview.systemDeltaTK } : {}),
    };
  }, [context, preview]);

  const previewing =
    previewContext !== null &&
    context !== null &&
    (previewContext.designFlowTempC !== context.designFlowTempC ||
      previewContext.systemDeltaTK !== context.systemDeltaTK);

  return {
    status,
    context,
    previewContext,
    preview,
    previewing,
    savedBy,
    jobId,
    error,
    saving,
    reload,
    setPreview,
    clearPreview,
    setDesignFlow,
  };
}
